import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { FENTON_WEIGHT, BHUTANI } from "@/lib/references";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, ReferenceArea, Legend, ScatterChart, Scatter, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, TrendingUp, Droplet, Wind, CircleDot, Plus } from "lucide-react";
import { fmtLocalDT } from "@/lib/clinical";

function GrowthChartCard({ data, patientName }) {
  return (
    <div className="card-clinical p-4">
      <div className="flex items-center gap-2 mb-2">
        <TrendingUp size={14} className="text-primary" />
        <div className="text-sm font-medium">Fenton weight-for-PMA</div>
        <div className="text-xs text-muted-foreground ml-2">
          (reference bands: 3rd — 50th — 97th percentile)
        </div>
      </div>
      <div className="h-72">
        <ResponsiveContainer>
          <LineChart data={data}>
            <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
            <XAxis dataKey="pma" type="number" domain={GROWTH_X_DOMAIN} stroke="#94a3b8" fontSize={11} label={GROWTH_X_LABEL} />
            <YAxis stroke="#94a3b8" fontSize={11} domain={GROWTH_Y_DOMAIN} label={GROWTH_Y_LABEL} />
            <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
            <Legend wrapperStyle={CHART_LEGEND_STYLE} />
            <Line type="monotone" dataKey="p3"  name="P3"  stroke="#f59e0b" strokeDasharray="4 4" dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="p50" name="P50" stroke="#0284c7" strokeWidth={2}         dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="p97" name="P97" stroke="#f59e0b" strokeDasharray="4 4" dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="patient" name={patientName} stroke="#10b981" strokeWidth={2} dot={GROWTH_PATIENT_DOT} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function BiliChartCard({ data }) {
  return (
    <div className="card-clinical p-4">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle size={14} className="status-photo" />
        <div className="text-sm font-medium">Bhutani bilirubin nomogram</div>
        <div className="text-xs text-muted-foreground ml-2">(low / high-intermediate / high-risk zones — plot Total serum bilirubin vs age in hours)</div>
      </div>
      <div className="h-72">
        <ResponsiveContainer>
          <LineChart data={data}>
            <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
            <XAxis dataKey="hr" type="number" domain={BILI_X_DOMAIN} stroke="#94a3b8" fontSize={11} label={BILI_X_LABEL} />
            <YAxis stroke="#94a3b8" fontSize={11} domain={BILI_Y_DOMAIN} label={BILI_Y_LABEL} />
            <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
            <Legend wrapperStyle={CHART_LEGEND_STYLE} />
            <Line type="monotone" dataKey="p40" name="Low"                stroke="#10b981" strokeDasharray="3 3" dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="p75" name="High-Intermediate" stroke="#f59e0b" strokeDasharray="4 4" dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="p95" name="High risk"          stroke="#ef4444" strokeWidth={2}       dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="patient" name="TSB"            stroke="#0284c7" strokeWidth={2}       dot={BILI_PATIENT_DOT} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="text-[11px] text-muted-foreground mt-2">
        Reference values are approximations for visual guidance — confirm risk zone with AAP/local phototherapy guidelines before intervention.
      </div>
    </div>
  );
}

// Stable object references outside the component so Recharts doesn't re-render on every parent update.
const CHART_TOOLTIP_STYLE = { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 };
const CHART_LEGEND_STYLE  = { fontSize: 11 };
const GROWTH_X_DOMAIN = [22, 42];
const GROWTH_Y_DOMAIN = [300, 4200];
const GROWTH_X_LABEL  = { value: "PMA (weeks)", position: "insideBottom", offset: -4, fontSize: 11 };
const GROWTH_Y_LABEL  = { value: "Weight (g)", angle: -90, position: "insideLeft", fontSize: 11 };
const GROWTH_PATIENT_DOT = { r: 4 };
const BILI_X_DOMAIN   = [0, 120];
const BILI_Y_DOMAIN   = [0, 22];
const BILI_X_LABEL    = { value: "Age (hours)", position: "insideBottom", offset: -4, fontSize: 11 };
const BILI_Y_LABEL    = { value: "TSB (mg/dL)", angle: -90, position: "insideLeft", fontSize: 11 };
const BILI_PATIENT_DOT = { r: 4 };

function pmaFor(patient, atIso) {
  const ga = patient.gestational_age_weeks || 40;
  const adm = new Date(patient.admission_date || Date.now());
  const t = atIso ? new Date(atIso) : new Date();
  const daysSinceAdm = Math.max(0, (t - adm) / 86400000);
  const dolAtAdm = patient.day_of_life || 0;
  const dol = dolAtAdm + daysSinceAdm;
  return ga + dol / 7;
}

function hoursSinceAdmission(patient, atIso) {
  const adm = new Date(patient.admission_date || Date.now());
  const t = atIso ? new Date(atIso) : new Date();
  return Math.max(0, (t - adm) / 3600000) + (patient.day_of_life || 0) * 24;
}

export default function GrowthTab({ patient }) {
  const [vitals, setVitals] = useState([]);
  const [invs, setInvs] = useState([]);
  const [lines, setLines] = useState([]);

  const load = useCallback(async () => {
    try {
      const [{ data: v }, { data: i }, { data: l }] = await Promise.all([
        api.get(`/vitals/${patient.id}`),
        api.get(`/investigations/${patient.id}`, { params: { category: "Bilirubin" } }),
        api.get(`/lines/${patient.id}`),
      ]);
      setVitals(v);
      setInvs(i);
      setLines(l);
    } catch (e) { console.warn("Growth tab load failed", e); }
  }, [patient.id]);
  useEffect(() => { load(); }, [load]);

  // --- Growth data (Fenton + patient trajectory) ---
  const growthData = useMemo(() => {
    // Build reference series across the Fenton PMA range, and add patient points
    const points = FENTON_WEIGHT.map(r => ({ pma: r.pma, p3: r.p3, p50: r.p50, p97: r.p97, patient: null }));
    const weighed = vitals.filter(v => v.weight_g);
    const patientPoints = weighed.map(v => ({ pma: Number(pmaFor(patient, v.at).toFixed(1)), patient: v.weight_g }));
    return [...points, ...patientPoints].sort((a, b) => a.pma - b.pma);
  }, [vitals, patient]);

  // --- Bilirubin data (Bhutani + patient TSB dots) ---
  const biliData = useMemo(() => {
    const base = BHUTANI.map(b => ({ hr: b.hr, p40: b.p40, p75: b.p75, p95: b.p95, patient: null }));
    const points = invs.map(i => {
      const t = Number(i.values?.Total);
      if (isNaN(t)) return null;
      return { hr: Number(hoursSinceAdmission(patient, i.at).toFixed(1)), patient: t };
    }).filter(Boolean);
    return [...base, ...points].sort((a, b) => a.hr - b.hr);
  }, [invs, patient]);

  const activeLines = lines.filter(l => l.active);

  const [proc, setProc] = useState({ kind: "UVC", operator: "", notes: "" });
  const addProcedure = async () => {
    if (!proc.operator) { toast.error("Operator required"); return; }
    try {
      await api.post("/procedures", { patient_id: patient.id, name: proc.kind, operator: proc.operator, notes: proc.notes });
      toast.success(`${proc.kind} logged`);
      setProc({ kind: "UVC", operator: "", notes: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="growth-tab">
      {/* Log line insertion */}
      <div className="card-clinical p-4">
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Log a line / procedure</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Select value={proc.kind} onValueChange={(v) => setProc((s) => ({ ...s, kind: v }))}>
            <SelectTrigger data-testid="proc-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["UVC", "UAC", "PICC", "PIV", "ETT", "Foley", "Intubation", "Lumbar puncture", "Exchange transfusion", "Chest tube"].map(k => (
                <SelectItem key={k} value={k}>{k}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input placeholder="Operator" value={proc.operator} onChange={(e) => setProc((s) => ({ ...s, operator: e.target.value }))} data-testid="proc-operator" />
          <Input className="md:col-span-2" placeholder="Notes / size / depth" value={proc.notes} onChange={(e) => setProc((s) => ({ ...s, notes: e.target.value }))} />
          <Button onClick={addProcedure} className="rounded-full" data-testid="proc-add"><Plus size={14} className="mr-1" /> Log</Button>
        </div>
      </div>

      {/* Line day counters */}
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2">
          <CircleDot size={12} /> Line-day counters
          {activeLines.length === 0 && <span className="normal-case tracking-normal">— no active lines</span>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {activeLines.map(l => {
            const bg = l.risk === "critical" ? "bg-critical" : l.risk === "warn" ? "bg-warn" : "bg-vacant";
            return (
              <div key={l.id} className="card-clinical p-4 flex items-start gap-3">
                <div className={`h-10 w-10 rounded-lg grid place-items-center ${bg}`}>
                  {l.kind === "UVC" || l.kind === "UAC" || l.kind === "PICC" ? <Droplet size={16} /> : <Wind size={16} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{l.kind} <span className="text-xs text-muted-foreground">· {l.name}</span></div>
                    {l.risk !== "ok" && <span className={`text-[10px] rounded-full px-2 py-0.5 ${bg}`}>{l.risk === "critical" ? "REMOVE" : "REVIEW"}</span>}
                  </div>
                  <div className="mt-1 tabular text-sm">
                    <span className="font-display font-semibold text-2xl">{Math.floor(l.days)}</span>
                    <span className="text-muted-foreground text-xs"> / {l.max_days} days</span>
                  </div>
                  <div className="text-xs text-muted-foreground">inserted {fmtLocalDT(l.inserted_at)} · {l.operator || "-"}</div>
                  <div className="mt-2">
                    <Button size="sm" variant="outline" className="rounded-full h-7 text-xs"
                      onClick={async () => {
                        try { await api.post(`/lines/${l.id}/remove`, { removed_reason: "clinical decision" }); toast.success("Line removed"); load(); }
                        catch { toast.error("Failed"); }
                      }}>Mark removed</Button>
                  </div>
                </div>
              </div>
            );
          })}
          {activeLines.length === 0 && (
            <div className="col-span-full text-xs text-muted-foreground bg-muted/40 rounded-lg px-4 py-3">
              Record UVC / UAC / PICC / ETT insertions in the Timeline → Procedures section — day counters will appear here with CLABSI-risk flags.
            </div>
          )}
        </div>
      </div>

      {/* Fenton Growth chart */}
      <GrowthChartCard data={growthData} patientName={patient.name} />

      {/* Bhutani bilirubin nomogram */}
      <BiliChartCard data={biliData} />
    </div>
  );
}
